/* cwclientelements
   copyright 2019 LOGILAB S.A. (Paris, FRANCE), all rights reserved.
   contact http://www.logilab.fr/ -- mailto:contact@logilab.fr
*/

import * as React from 'react';
import {providers} from '@logilab/cwclientlibjs';
import {
    EntityRelationsGroupProps,
    EntityRelationsGroup,
    ENTITY_RELATIONS_GROUP_DEFAULT_PROPS,
} from './EntityRelationsGroup';
import {ProvidersContext, TranslationContext} from './commons';

/**
 * The props for this component
 */
export interface MergedEntityRelationsGroupProps
    extends EntityRelationsGroupProps {
    /**
     * Renders the edit button for the group
     * @param component The current component (this)
     */
    renderEditButton: (
        component: EntityRelationsGroup<MergedEntityRelationsGroupProps>
    ) => JSX.Element;
    /**
     * Renders the title for the group
     * @param component The current component (this)
     */
    renderTitle: (
        component: EntityRelationsGroup<MergedEntityRelationsGroupProps>
    ) => JSX.Element;
    /**
     * Renders a particular relation value
     * @param component The current component (this)
     */
    renderValue: (
        component: EntityRelationsGroup<MergedEntityRelationsGroupProps>,
        index: number
    ) => JSX.Element;
}

/**
 * State for this component
 */
export interface MergedEntityRelationsGroupState {
    /**
     * The starting index of values for a specific relation in the common arrays of values and names
     */
    relationsStart: number[];
    /**
     * The number of values of each specific relation in the common arrays of values and names
     */
    relationsCount: number[];
    /**
     * The EID of the related entities
     */
    values: number[];
    /**
     * The displayable names of the related entities
     */
    displays: (JSX.Element | null)[];
}

/**
 * Default props for a merged entity relations group
 */
export const MERGED_ENTITY_RELATIONS_GROUP_DEFAULT_PROPS = {
    ...ENTITY_RELATIONS_GROUP_DEFAULT_PROPS,
    renderEditButton: (
        component: EntityRelationsGroup<MergedEntityRelationsGroupProps>
    ) => {
        return (
            <TranslationContext.Consumer>
                {(tc) => (
                    <button
                        onClick={(event) =>
                            component.props.onClickEdit(
                                component.props.relations
                            )
                        }
                    >
                        {tc.translate('edit')}
                    </button>
                )}
            </TranslationContext.Consumer>
        );
    },
    renderTitle: (
        component: EntityRelationsGroup<MergedEntityRelationsGroupProps>
    ) => {
        return (
            <TranslationContext.Consumer>
                {(tc) => (
                    <span>
                        {tc.translate(component.props.relations[0].name)}
                    </span>
                )}
            </TranslationContext.Consumer>
        );
    },
    renderValue: (
        component: EntityRelationsGroup<
            MergedEntityRelationsGroupProps,
            MergedEntityRelationsGroupState
        >,
        index: number
    ) => {
        let eid = component.state.values[index];
        let display = component.state.displays[index];
        let displayComponent =
            display !== null ? display : <span>{eid.toString()}</span>;
        return (
            <span key={`${index}}`}>
                {index > 0 ? <span>{', '}</span> : null}
                <a
                    href={component.props.getTargetLink(eid)}
                    onClick={(event) => {
                        event.preventDefault();
                        component.props.onClickGotoTarget(eid);
                    }}
                >
                    {displayComponent}
                </a>
            </span>
        );
    },
    render: (
        component: EntityRelationsGroup<
            MergedEntityRelationsGroupProps,
            MergedEntityRelationsGroupState
        >
    ) => {
        return (
            <tr className={component.props.className}>
                <td>{component.props.renderTitle(component)}</td>
                <td>
                    {component.state.values.map((eid: number, index: number) =>
                        component.props.renderValue(component, index)
                    )}{' '}
                    {component.props.editable === true
                        ? component.props.renderEditButton(component)
                        : null}
                </td>
            </tr>
        );
    },
};

/**
 * Component for rendering an arbitrary entity relation
 */
export class MergedEntityRelationsGroup extends EntityRelationsGroup<
    MergedEntityRelationsGroupProps,
    MergedEntityRelationsGroupState
> {
    public static contextType = ProvidersContext;
    public context!: React.ContextType<typeof ProvidersContext>;
    public static defaultProps = MERGED_ENTITY_RELATIONS_GROUP_DEFAULT_PROPS;
    state: MergedEntityRelationsGroupState;
    _isMounted: boolean;

    constructor(props: Readonly<MergedEntityRelationsGroupProps>) {
        super(props);
        let relationsStart: number[] = [];
        let relationsCount: number[] = [];
        let values: number[] = [];
        let currentIndex = 0;
        this.props.relations.forEach(
            (relation: providers.EntityRelationSchema) => {
                relationsStart.push(currentIndex);
                let relatedEids =
                    this.props.entity[
                        providers.getUniqueRelationName(relation)
                    ];
                if (relatedEids !== null && relatedEids !== undefined) {
                    let x = relatedEids as number[];
                    relationsCount.push(x.length);
                    values.push(...x);
                    currentIndex += x.length;
                } else {
                    relationsCount.push(0);
                }
            }
        );
        let names = new Array(values.length).fill(null);
        this.state = {
            relationsStart: relationsStart,
            relationsCount: relationsCount,
            values: values,
            displays: names,
        };
        this._isMounted = false;
    }

    componentDidMount() {
        this._isMounted = true;
        let self = this;
        for (let i = 0; i !== this.props.relations.length; i++) {
            let relation = this.props.relations[i];
            this.context.schemaProvider
                .getEntitySchemaById(
                    this.props.entityIsSubject === true
                        ? relation.to
                        : relation.from
                )
                .then((schema) => {
                    if (schema === null) {
                        return;
                    }
                    for (let j = 0; j !== self.state.relationsCount[i]; j++) {
                        let value =
                            self.state.values[self.state.relationsStart[i] + j];
                        self.context.entityProvider
                            .getEntityGivenSchema(value, schema)
                            .then((entity) => {
                                if (entity === null) {
                                    return;
                                }
                                if (self._isMounted) {
                                    self.state.displays[
                                        self.state.relationsStart[i] + j
                                    ] = self.props.renderDisplayValue(
                                        entity,
                                        schema
                                    );
                                    self.setState(self.state);
                                }
                            })
                            .catch((reason: any) => {
                                console.log(reason);
                            });
                    }
                })
                .catch((reason: any) => {
                    console.log(reason);
                });
        }
    }

    componentWillUnmount() {
        this._isMounted = false;
    }

    render(): JSX.Element {
        return this.props.render(this);
    }
}
